import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { AiOutlineCloudUpload, AiOutlineCopy, AiOutlineCheck } from 'react-icons/ai';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const AnnexureBModule = ({ rowData, extraData, onDataChange }) => {
    const [dataRows, setDataRows] = useState([]);

    const [metadata, setMetadata] = useState({
        tradeName: "",
        gstin: "",
        fromPeriod: "",
        toPeriod: ""
    });

    useEffect(() => {
        if (rowData?.client) {
            setMetadata(prev => ({
                ...prev,
                tradeName: rowData.client.firm_name || "",
                gstin: rowData.client.gstin_no || "N/A",
                fromPeriod: extraData?.from ? `${String(extraData.from.getMonth() + 1).padStart(2, '0')}${extraData.from.getFullYear()}` : "",
                toPeriod: extraData?.to ? `${String(extraData.to.getMonth() + 1).padStart(2, '0')}${extraData.to.getFullYear()}` : ""
            }));
        }
    }, [rowData, extraData]);

    const [stats, setStats] = useState({ B2B: 0, CDNR: 0, IMPG: 0, IMPG_SEZ: 0, blankGstinFallback: 0 });
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [originalFilename, setOriginalFilename] = useState("");
    const [reversalOthers, setReversalOthers] = useState({ cgst: 0, sgst: 0, igst: 0, cess: 0 });
    const fileInputRef = useRef(null);

    // ── localStorage key scoped to this client ──────────────────────────────
    const lsKey = `annexure_b_new_${rowData?.client?.id || rowData?.id || 'default'}`;

    // ── Load saved data on mount ────────────────────────────────────────────
    useEffect(() => {
        try {
            const saved = localStorage.getItem(lsKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.dataRows?.length > 0) setDataRows(parsed.dataRows);
                if (parsed.stats) setStats(parsed.stats);
                if (parsed.reversalOthers) setReversalOthers(parsed.reversalOthers);
            }
        } catch (err) {
            console.warn('AnnexureB: failed to load from localStorage', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lsKey]);

    // ── Persist reversalOthers whenever the user edits Row 4 ───────────────
    useEffect(() => {
        if (dataRows.length === 0) return; // don't overwrite if nothing loaded yet
        try {
            const saved = localStorage.getItem(lsKey);
            const parsed = saved ? JSON.parse(saved) : {};
            localStorage.setItem(lsKey, JSON.stringify({
                ...parsed,
                dataRows: dataRows,
                stats: stats,
                reversalOthers: reversalOthers
            }));
        } catch (e) { /* ignore */ }
    }, [reversalOthers, dataRows, stats, lsKey]);

    // ── Helpers ────────────────────────────────────────────────────────────
    const formatDate = (raw) => {
        // Output: dd-mm-yyyy (per new spec)
        if (!raw) return "";
        if (typeof raw === "number") {
            const d = new Date(Math.round((raw - 25569) * 86400 * 1000));
            const dd = String(d.getUTCDate()).padStart(2, "0");
            const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
            const yyyy = d.getUTCFullYear();
            return `${dd}-${mm}-${yyyy}`;
        }
        let s = String(raw).trim();
        // Normalize separator
        s = s.replace(/\//g, "-");
        // yyyy-mm-dd
        if (s.length >= 10 && s.indexOf("-") === 4) {
            const parts = s.split(/[- T]/);
            return `${parts[2].substring(0, 2).padStart(2, "0")}-${parts[1].padStart(2, "0")}-${parts[0]}`;
        }
        const parts = s.split("-");
        if (parts.length === 3 && parts[2].length === 2) {
            const yr = parseInt(parts[2]);
            return `${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${yr > 50 ? "19" : "20"}${parts[2]}`;
        }
        if (parts.length === 3) {
            return `${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${parts[2]}`;
        }
        return s;
    };

    // Convert various 2B return-period formats → "mm-yyyy"
    // PRIMARY: Handles "Mmm space yy" format from General formatted Excel (Jan 26, Apr 25, Dec 26)
    // Also handles: "Apr-25", "Apr 25", "Apr  25", "Apr - 25", "Apr-2025",
    //               "April 25", "APR-25", "01 26", Excel date serials, etc.
    const formatReturnPeriod = (raw) => {
        if (raw === null || raw === undefined || raw === "") return "";

        const monthMap = {
            jan: "01", january: "01",
            feb: "02", february: "02",
            mar: "03", march: "03",
            apr: "04", april: "04",
            may: "05",
            jun: "06", june: "06",
            jul: "07", july: "07",
            aug: "08", august: "08",
            sep: "09", sept: "09", september: "09",
            oct: "10", october: "10",
            nov: "11", november: "11",
            dec: "12", december: "12"
        };

        // 1. If it's an Excel date serial number, convert to date and extract mm-yyyy
        if (typeof raw === "number") {
            const d = new Date(Math.round((raw - 25569) * 86400 * 1000));
            const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
            const yyyy = d.getUTCFullYear();
            return `${mm}-${yyyy}`;
        }

        // 2. Convert to string and normalize
        let s = String(raw).trim();
        if (!s) return "";

        // ✨ PRIMARY PATTERN (NEW): Handle "Mmm space yy" format from General formatted Excel cells
        // Examples: "Jan 26", "Apr 25", "Dec 26", "jan 26", "JAN 26", "Jan 2026"
        const mmmYyPattern = /^([A-Za-z]{3})\s+(\d{2,4})$/;
        const mmmMatch = s.match(mmmYyPattern);
        if (mmmMatch) {
            const monthName = mmmMatch[1].toLowerCase();
            const mm = monthMap[monthName];
            
            if (mm) {
                let yy = mmmMatch[2];
                
                // Convert 2-digit year to 4-digit
                if (yy.length === 2) {
                    const yyNum = parseInt(yy, 10);
                    yy = (yyNum > 50 ? "19" : "20") + yy;
                }
                
                return `${mm}-${yy}`;
            }
        }

        // 3. Handle numeric "MM space YY" format (backup)
        // Examples: "01 26", "04 25", "12 26"
        const numericPattern = /^(\d{1,2})\s+(\d{2,4})$/;
        const numMatch = s.match(numericPattern);
        if (numMatch) {
            let mm = numMatch[1].padStart(2, "0");
            let yy = numMatch[2];
            
            if (yy.length === 2) {
                const yyNum = parseInt(yy, 10);
                yy = (yyNum > 50 ? "19" : "20") + yy;
            }
            
            return `${mm}-${yy}`;
        }

        // 4. Handle Excel auto-formatted dates like "January' 26", "January 26", "January 26, 2026"
        const excelDatePatterns = [
            /^([A-Za-z]+)['\s]+(\d{2,4})$/,           // "January' 26" or "January 26"
            /^([A-Za-z]+)\s+\d{1,2},?\s+(\d{4})$/,   // "January 26, 2026"
            /^([A-Za-z]+)-(\d{1,2})-(\d{4})$/        // "January-26-2026"
        ];

        for (const pattern of excelDatePatterns) {
            const match = s.match(pattern);
            if (match) {
                const mon = monthMap[match[1].toLowerCase()];
                if (mon) {
                    let yr = match[2];
                    if (yr.length === 2) {
                        yr = (parseInt(yr, 10) > 50 ? "19" : "20") + yr;
                    }
                    return `${mon}-${yr}`;
                }
            }
        }

        // 5. Normalize separators and match standard patterns
        s = s.replace(/[\s\-\.\/]+/g, "-");

        // 6. Match: <month-letters>-<digits>
        const m = s.match(/^([A-Za-z]+)-(\d{2,4})$/);
        if (m) {
            const mon = monthMap[m[1].toLowerCase()];
            let yr = m[2];
            if (yr.length === 2) {
                yr = (parseInt(yr, 10) > 50 ? "19" : "20") + yr;
            } else if (yr.length === 3) {
                yr = "2" + yr;
            }
            if (mon) return `${mon}-${yr}`;
        }

        // 7. Already in mm-yyyy or mm/yyyy form → normalize
        const numeric = s.match(/^(\d{1,2})-(\d{4})$/);
        if (numeric) {
            return `${numeric[1].padStart(2, "0")}-${numeric[2]}`;
        }

        // 8. Extract month and year from any date-like string as fallback
        for (const [monthName, monthNum] of Object.entries(monthMap)) {
            const regex = new RegExp(monthName, 'i');
            if (regex.test(s)) {
                const yearMatch = s.match(/(\d{4}|\d{2})/);
                if (yearMatch) {
                    let yr = yearMatch[1];
                    if (yr.length === 2) {
                        yr = (parseInt(yr, 10) > 50 ? "19" : "20") + yr;
                    }
                    return `${monthNum}-${yr}`;
                }
            }
        }

        // 9. Fallback: return original (so user can spot what didn't parse)
        return String(raw).trim();
    };

    const findSheet = (wb, names) => {
        for (const name of names) {
            const found = wb.SheetNames.find(
                s => s.trim().toLowerCase() === name.trim().toLowerCase()
            );
            if (found) return wb.Sheets[found];
        }
        return null;
    };

    const safeNum = (v) => {
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
    };

    const safeStr = (v) => (v === null || v === undefined ? "" : String(v).trim());

    const formatINR = (val) => {
        const n = parseFloat(val);
        if (isNaN(n) || n === 0) return "0.00";
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(n);
    };

    // ── New 21-column logic ────────────────────────────────────────────────

    // Col 10: Category of Supplies
    const deriveCategory = (gstin, hsn, inwardType) => {
        const g = safeStr(gstin);
        const code = safeStr(hsn);
        
        if (!g) {
            // For Imports, we don't automatically force Input Services just because GSTIN is blank
            if (inwardType === "Import of Goods" || inwardType === "Import of Services") {
                if (code.startsWith("99") || code.startsWith("00")) return "Input Services";
                return "Inputs";
            }
            return "Input Services"; // Blank GSTIN override for regular B2B/CDNR
        }
        
        if (code.startsWith("99") || code.startsWith("00")) return "Input Services";
        return "Inputs";
    };

    // Col 11: HSN/SAC
    const deriveHsn = (gstin, sourceHsn, inwardType) => {
        const g = safeStr(gstin);
        if (!g) {
            // Do not default to 9965 for Imports
            if (inwardType === "Import of Goods" || inwardType === "Import of Services") {
                return safeStr(sourceHsn);
            }
            return "9965"; // Blank GSTIN → default to 9965 for regular B2B/CDNR
        }
        return safeStr(sourceHsn);
    };

    // Col 2: Type of Inward Supply
    const deriveInwardType = (source, reverseCharge, gstin) => {
        if (source === 'B2B') {
            if (safeStr(reverseCharge).toLowerCase() === 'yes') {
                return safeStr(gstin)
                    ? "Inward Supplies liable to Reverse Charge (Registered Persons)"
                    : "Inward Supplies liable to Reverse Charge (Unregistered Persons)";
            }
            return "Inward Supplies from Registered Persons";
        }
        if (source === 'CDNR') {
            if (safeStr(reverseCharge).toLowerCase() === 'yes') {
                return "Inward Supplies liable to Reverse Charge (Registered Persons)";
            }
            return "Inward Supplies from Registered Persons";
        }
        if (source === 'IMPG') return "Import of Goods";
        if (source === 'IMPG_SEZ') return "Import of Goods(Supplies from SEZ to DTA)";
        return "";
    };

    // Col 3: Type of Document
    const deriveDocType = (source, noteType) => {
        if (source === 'CDNR') {
            return safeStr(noteType).toUpperCase() === 'D' ? "Debit Note" : "Credit Note";
        }
        return "Invoice/Bill of Entry";
    };

    const updateRowField = (index, field, value) => {
        const newRows = [...dataRows];
        newRows[index][field] = value;
        setDataRows(newRows);
        try {
            localStorage.setItem(lsKey, JSON.stringify({ dataRows: newRows, stats }));
        } catch (e) { /* ignore */ }
    };

    // ── Col 17 (ITC Blocked u/s 17(5)) cascade ─────────────────────────────
    // Yes → auto Col 18=No, Col 19=0, Col 20=totalITC
    // No  → reset: Col 18=Yes, Col 19=totalITC, Col 20=0
    // Partially → keep existing, enable manual edit
    const handleItcBlockedChange = (index, value) => {
        setDataRows(prev => {
            const next = prev.map((r, i) => {
                if (i !== index) return r;
                const totalItc = (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0) + (r.cess || 0);
                const updated = { ...r, itcBlocked: value };
                if (value === "Yes") {
                    updated.eligibleItc = "No";
                    updated.ineligibleItc = totalItc;
                } else if (value === "No") {
                    updated.eligibleItc = "Yes";
                    updated.ineligibleItc = 0;
                }
                return updated;
            });
            try { localStorage.setItem(lsKey, JSON.stringify({ dataRows: next, stats })); } catch (e) { }
            return next;
        });
    };

    // ── Col 18 (Eligible for ITC) cascade ──────────────────────────────────
    // Only changes anything if Col 17 !== "Yes" (Col 17="Yes" forces Col 18="No")
    // Yes → Col 19=totalITC, Col 20=0
    // No  → Col 19=0, Col 20=totalITC (then user can manually edit)
    // Partially → keep existing
    const handleEligibleItcChange = (index, value) => {
        setDataRows(prev => {
            const next = prev.map((r, i) => {
                if (i !== index) return r;
                const totalItc = (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0) + (r.cess || 0);
                const updated = { ...r, eligibleItc: value };
                if (r.itcBlocked !== "Yes") {
                    if (value === "Yes") updated.ineligibleItc = 0;
                    else if (value === "No") updated.ineligibleItc = totalItc;
                }
                return updated;
            });
            try { localStorage.setItem(lsKey, JSON.stringify({ dataRows: next, stats })); } catch (e) { }
            return next;
        });
    };

    // ── User edits Col 19 (Eligible Amount) ──── Col 20 = totalITC - Col 19
    const handleEligibleAmtChange = (index, value) => {
        setDataRows(prev => {
            const next = prev.map((r, i) => {
                if (i !== index) return r;
                const totalItc = (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0) + (r.cess || 0);
                let elig = parseFloat(value);
                if (isNaN(elig)) elig = 0;
                elig = Math.max(0, Math.min(totalItc, elig));
                return { ...r, ineligibleItc: Math.round((totalItc - elig) * 100) / 100 };
            });
            try { localStorage.setItem(lsKey, JSON.stringify({ dataRows: next, stats })); } catch (e) { }
            return next;
        });
    };

    // ── User edits Col 20 (Ineligible Amount) ──── Col 19 = totalITC - Col 20
    const handleIneligibleAmtChange = (index, value) => {
        setDataRows(prev => {
            const next = prev.map((r, i) => {
                if (i !== index) return r;
                const totalItc = (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0) + (r.cess || 0);
                let inelig = parseFloat(value);
                if (isNaN(inelig)) inelig = 0;
                inelig = Math.max(0, Math.min(totalItc, inelig));
                return { ...r, ineligibleItc: Math.round(inelig * 100) / 100 };
            });
            try { localStorage.setItem(lsKey, JSON.stringify({ dataRows: next, stats })); } catch (e) { }
            return next;
        });
    };

    // Whether Col 19/20 are editable for a given row
    const isAmtEditable = (r) => {
        if (r.itcBlocked === "Yes") return false;          // fully blocked → locked
        if (r.itcBlocked === "Partially") return true;     // partial blocked → editable
        // itcBlocked === "No"
        if (r.eligibleItc === "Yes") return false;         // fully eligible → locked
        return true;                                       // No or Partially → editable
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);

        // Capture filename without extension for JSON export later
        const fname = file.name.replace(/\.(xlsx|xls)$/i, "");
        setOriginalFilename(fname);

        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer);

            const finalRows = [];
            const runningStats = { B2B: 0, CDNR: 0, IMPG: 0, IMPG_SEZ: 0, blankGstinFallback: 0 };
            const meta = { ...metadata };

            // 0. Metadata from "Read me"
            const readme = findSheet(wb, ['Read me', 'README']);
            if (readme) {
                const rows = XLSX.utils.sheet_to_json(readme, { header: 1, defval: "" });
                if (rows.length > 6) {
                    meta.gstin = safeStr(rows[5][2]) || meta.gstin;
                    meta.tradeName = safeStr(rows[6][2]) || meta.tradeName;
                }
            }
            setMetadata(meta);

            // ─── 1. B2B ──────────────────────────────────────────────────
            const b2bSheet = findSheet(wb, ['B2B']);
            if (b2bSheet) {
                const rows = XLSX.utils.sheet_to_json(b2bSheet, { header: 1, defval: "" });
                // Data starts from row index 6 (row 7)
                for (let i = 6; i < rows.length; i++) {
                    const r = rows[i];
                    if (!r.some(v => v !== "")) continue;
                    if (String(r[0]).toLowerCase().includes("gstin")) continue;
                    if (String(r[2]).toLowerCase().includes("invoice")) continue;

                    const gstin = safeStr(r[0]);
                    const reverseCharge = safeStr(r[7]); // Col H
                    const sourceHsn = safeStr(r[18]);

                    const inwardType = deriveInwardType('B2B', reverseCharge, gstin);
                    finalRows.push({
                        source: 'B2B',
                        inwardType: inwardType,
                        docType: deriveDocType('B2B'),
                        gstin: gstin,
                        supplierName: safeStr(r[1]),
                        docNo: safeStr(r[2]),
                        date: formatDate(r[4]),
                        portCode: "",
                        value: safeNum(r[9]),
                        category: deriveCategory(gstin, sourceHsn, inwardType),
                        hsn: deriveHsn(gstin, sourceHsn, inwardType),
                        cgst: safeNum(r[11]),
                        sgst: safeNum(r[12]),
                        igst: safeNum(r[10]),
                        cess: safeNum(r[13]), // Col N
                        itcBlocked: "No",
                        eligibleItc: "Yes",
                        ineligibleItc: 0,
                        returnPeriod: formatReturnPeriod(r[14]), // Col O
                        // Keep original HSN for reference
                        _sourceHsn: sourceHsn
                    });
                    if (!gstin) runningStats.blankGstinFallback++;
                    runningStats.B2B++;
                }
            }

            // ─── 2. B2B-CDNR ─────────────────────────────────────────────
            const cdnrSheet = findSheet(wb, ['B2B-CDNR', 'CDNR']);
            if (cdnrSheet) {
                const rows = XLSX.utils.sheet_to_json(cdnrSheet, { header: 1, defval: "" });
                for (let i = 6; i < rows.length; i++) {
                    const r = rows[i];
                    if (!r.slice(0, 14).some(v => v !== "")) continue;
                    if (String(r[0]).toLowerCase().includes("gstin")) continue;
                    if (String(r[2]).toLowerCase().includes("invoice") || String(r[2]).toLowerCase().includes("note")) continue;

                    const gstin = safeStr(r[0]);
                    const noteType = safeStr(r[3]); // Col D — "C" or "D"
                    const reverseCharge = safeStr(r[8]); // Col I
                    const sourceHsn = safeStr(r[18]);

                    // Sign convention: Credit notes reduce ITC → keep negative
                    const sign = noteType.toUpperCase() === 'D' ? 1 : -1;

                    const inwardType = deriveInwardType('CDNR', reverseCharge, gstin);
                    finalRows.push({
                        source: 'CDNR',
                        inwardType: inwardType,
                        docType: deriveDocType('CDNR', noteType),
                        gstin: gstin,
                        supplierName: safeStr(r[1]),
                        docNo: safeStr(r[2]),
                        date: formatDate(r[5]),
                        portCode: "",
                        value: safeNum(r[10]) * sign,
                        category: deriveCategory(gstin, sourceHsn, inwardType),
                        hsn: deriveHsn(gstin, sourceHsn, inwardType),
                        cgst: safeNum(r[12]) * sign,
                        sgst: safeNum(r[13]) * sign,
                        igst: safeNum(r[11]) * sign,
                        cess: safeNum(r[14]) * sign, // Col O
                        itcBlocked: "No",
                        eligibleItc: "Yes",
                        ineligibleItc: 0,
                        returnPeriod: formatReturnPeriod(r[15]), // Col P
                        _sourceHsn: sourceHsn
                    });
                    if (!gstin) runningStats.blankGstinFallback++;
                    runningStats.CDNR++;
                }
            }

            // ─── 3. IMPG ─────────────────────────────────────────────────
            const impgSheet = findSheet(wb, ['IMPG']);
            if (impgSheet) {
                const rows = XLSX.utils.sheet_to_json(impgSheet, { header: 1, defval: "" });
                for (let i = 6; i < rows.length; i++) {
                    const r = rows[i];
                    if (!r[2] || String(r[2]).toLowerCase().includes("bill of entry")) continue;

                    const gstin = ""; // IMPG has no supplier GSTIN
                    const sourceHsn = safeStr(r[7]);

                    const inwardType = deriveInwardType('IMPG');
                    finalRows.push({
                        source: 'IMPG',
                        inwardType: inwardType,
                        docType: deriveDocType('IMPG'),
                        gstin: gstin,
                        supplierName: "Import of Goods",
                        docNo: safeStr(r[2]),
                        date: formatDate(r[3]),
                        portCode: safeStr(r[1]), // Col B
                        value: safeNum(r[4]),
                        category: deriveCategory(gstin, sourceHsn, inwardType),
                        hsn: deriveHsn(gstin, sourceHsn, inwardType),
                        cgst: 0,
                        sgst: 0,
                        igst: safeNum(r[5]),
                        cess: safeNum(r[6]), // Col G
                        itcBlocked: "No",
                        eligibleItc: "Yes",
                        ineligibleItc: 0,
                        returnPeriod: formatReturnPeriod(r[8]), // Col I
                        _sourceHsn: sourceHsn
                    });
                    runningStats.blankGstinFallback++; // IMPG always blank GSTIN
                    runningStats.IMPG++;
                }
            }

            // ─── 4. IMPG-SEZ ─────────────────────────────────────────────
            const impgSezSheet = findSheet(wb, ['IMPG SEZ', 'IMPG_SEZ', 'IMPG-SEZ', 'IMPGSEZ', 'IMPG (SEZ)']);
            if (impgSezSheet) {
                const rows = XLSX.utils.sheet_to_json(impgSezSheet, { header: 1, defval: "" });
                for (let i = 6; i < rows.length; i++) {
                    const r = rows[i];
                    if (!r[4] || String(r[4]).toLowerCase().includes("date")) continue;

                    const gstin = safeStr(r[0]); // SEZ supplier GSTIN if present
                    const sourceHsn = safeStr(r[9]);

                    const inwardType = deriveInwardType('IMPG_SEZ');
                    finalRows.push({
                        source: 'IMPG_SEZ',
                        inwardType: inwardType,
                        docType: deriveDocType('IMPG_SEZ'),
                        gstin: gstin,
                        supplierName: safeStr(r[1]) || "SEZ Supplier",
                        docNo: safeStr(r[4]),
                        date: formatDate(r[5]),
                        portCode: safeStr(r[3]), // Col D
                        value: safeNum(r[6]),
                        category: deriveCategory(gstin, sourceHsn, inwardType),
                        hsn: deriveHsn(gstin, sourceHsn, inwardType),
                        cgst: 0,
                        sgst: 0,
                        igst: safeNum(r[7]),
                        cess: safeNum(r[8]), // Col I
                        itcBlocked: "No",
                        eligibleItc: "Yes",
                        ineligibleItc: 0,
                        returnPeriod: formatReturnPeriod(r[8]), // Col I
                        _sourceHsn: sourceHsn
                    });
                    if (!gstin) runningStats.blankGstinFallback++;
                    runningStats.IMPG_SEZ++;
                }
            }

            setDataRows(finalRows);
            setStats(runningStats);

            // Console validation log
            console.log("─── Annexure B Import Log ───");
            console.log(`B2B rows: ${runningStats.B2B}`);
            console.log(`CDNR rows: ${runningStats.CDNR}`);
            console.log(`IMPG rows: ${runningStats.IMPG}`);
            console.log(`IMPG-SEZ rows: ${runningStats.IMPG_SEZ}`);
            console.log(`Total output rows: ${finalRows.length}`);
            console.log(`Blank-GSTIN fallback applied (HSN=9965, Category=Input Services): ${runningStats.blankGstinFallback}`);

            // Persist
            try {
                localStorage.setItem(lsKey, JSON.stringify({ dataRows: finalRows, stats: runningStats }));
            } catch (err) {
                console.warn('AnnexureB: failed to save to localStorage', err);
            }
        } catch (err) {
            console.error("Upload failed:", err);
            alert("Failed to parse the GSTR-2B file. Check console for details.");
        } finally {
            setLoading(false);
        }
    };

    // ── Bubble data up to parent if onDataChange provided ──────────────────
    useEffect(() => {
        if (onDataChange) {
            const rows = dataRows.map((r, idx) => {
                const itc = r.cgst + r.sgst + r.igst + r.cess;
                return [
                    idx + 1, r.inwardType, r.docType, r.gstin, r.supplierName, r.docNo,
                    r.date, r.portCode, r.value, r.category, r.hsn,
                    r.cgst, r.sgst, r.igst, r.cess, itc,
                    r.itcBlocked, r.eligibleItc, itc, r.ineligibleItc, r.returnPeriod
                ];
            });
            onDataChange(rows);
        }
    }, [dataRows, onDataChange]);

    // ── Download as JSON in GST-portal Annexure B format ──────────────────
    const handleDownloadJSON = () => {
        if (dataRows.length === 0) return;
        const { gstin, fromPeriod, toPeriod } = metadata;
        const round2 = (n) => Math.round(n * 100) / 100;

        let revCGST = 0, revSGST = 0, revIGST = 0, revCess = 0;

        const invoices = dataRows.map((r, idx) => {
            const totCredit = round2(
                (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0) + (r.cess || 0)
            );

            // Compute eligible vs ineligible ITC amounts based on dropdown
            let elgItcAmt, inelgItcAmt;
            if (r.eligibleItc === "Yes") {
                elgItcAmt = totCredit;
                inelgItcAmt = 0;
            } else if (r.eligibleItc === "No") {
                elgItcAmt = 0;
                inelgItcAmt = totCredit;
            } else {
                // Partially → use user's explicit ineligibleItc
                inelgItcAmt = round2(+(r.ineligibleItc || 0));
                elgItcAmt = round2(totCredit - inelgItcAmt);
            }

            // Accumulate reversal totals (Rule 38/42/43 + Sec 17(5))
            if (r.itcBlocked === "Yes") {
                revCGST += r.cgst || 0;
                revSGST += r.sgst || 0;
                revIGST += r.igst || 0;
                revCess += r.cess || 0;
            }

            return {
                sno: idx + 1,
                typeOfInwardSupply: r.inwardType || "",
                istype: r.docType || "",
                stin: r.gstin || "",
                snm: r.supplierName || "",
                inum: (r.docNo || "").replace(/\s+/g, ""),
                idt: r.date || "",
                portCode: r.portCode || "",
                val: round2(Math.abs(r.value || 0)),
                catOfInv: r.category || "",
                hsnSac: String(r.hsn || ""),
                camt: round2(Math.abs(r.cgst || 0)),
                samt: round2(Math.abs(r.sgst || 0)),
                iamt: round2(Math.abs(r.igst || 0)),
                cessamt: round2(Math.abs(r.cess || 0)),
                totCredit: round2(Math.abs(totCredit)),
                isItcBlocked: r.itcBlocked || "No",
                elgItc: r.eligibleItc || "Yes",
                elgItcAmt: round2(Math.abs(elgItcAmt)),
                returnPeriod2B: formatReturnPeriod(r.returnPeriod) || "",
                inelgItcAmt: round2(Math.abs(inelgItcAmt))
            };
        });

        const filename = originalFilename || (gstin || "annexure_b");

        const payload = {
            gstin: gstin || "",
            fromFp: fromPeriod || "",
            toFp: toPeriod || "",
            version: "1.0",
            revCGST: round2(revCGST),
            revSGST: round2(revSGST),
            revIGST: round2(revIGST),
            revCess: round2(revCess),
            revOthCGST: round2(+reversalOthers.cgst || 0),
            revOthSGST: round2(+reversalOthers.sgst || 0),
            revOthIGST: round2(+reversalOthers.igst || 0),
            revOthCess: round2(+reversalOthers.cess || 0),
            stmt: [{
                filename: filename,
                invoices: invoices
            }]
        };

        const json = JSON.stringify(payload);
        const blob = new Blob([json], { type: 'application/json' });
        saveAs(blob, `${filename}.json`);

        console.log("─── Annexure B JSON Export ───");
        console.log(`File: ${filename}.json`);
        console.log(`Invoices: ${invoices.length}`);
        console.log(`Reversals (Rule 38/42/43 + Sec 17(5)): CGST=${round2(revCGST)}, SGST=${round2(revSGST)}, IGST=${round2(revIGST)}, Cess=${round2(revCess)}`);
    };

    // ── Download as XLSX with NEW 21-column format + live formulas + dropdowns ──
    const handleDownload = async () => {
        if (dataRows.length === 0) return;

        const { tradeName, gstin, fromPeriod, toPeriod } = metadata;

        // Shared summary computations
        const totalCgst = dataRows.reduce((acc, row) => acc + (row.cgst || 0), 0);
        const totalSgst = dataRows.reduce((acc, row) => acc + (row.sgst || 0), 0);
        const totalIgst = dataRows.reduce((acc, row) => acc + (row.igst || 0), 0);
        const totalCess = dataRows.reduce((acc, row) => acc + (row.cess || 0), 0);

        const blockedRows = dataRows.filter(r => r.itcBlocked === "Yes");
        const rev2Cgst = blockedRows.reduce((acc, row) => acc + (row.cgst || 0), 0);
        const rev2Sgst = blockedRows.reduce((acc, row) => acc + (row.sgst || 0), 0);
        const rev2Igst = blockedRows.reduce((acc, row) => acc + (row.igst || 0), 0);
        const rev2Cess = blockedRows.reduce((acc, row) => acc + (row.cess || 0), 0);

        const summaryRow1 = { cgst: totalCgst, sgst: totalSgst, igst: totalIgst, cess: totalCess };
        const summaryRow2 = { cgst: rev2Cgst, sgst: rev2Sgst, igst: rev2Igst, cess: rev2Cess };

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Annexure B');

        // Styles
        const headerStyle = {
            font: { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } },
            alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
            border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
        };
        const summaryLabelStyle = {
            font: { bold: true, color: { argb: 'FF1E293B' }, name: 'Arial', size: 10 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } },
            alignment: { horizontal: 'left', vertical: 'middle' },
            border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
        };
        const summaryHeaderStyle = { ...summaryLabelStyle, alignment: { horizontal: 'center', vertical: 'middle' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } }, font: { ...summaryLabelStyle.font, color: { argb: 'FFFFFFFF' } } };
        const summaryValueStyle = {
            font: { bold: false, color: { argb: 'FF1E293B' }, name: 'Arial', size: 10 },
            alignment: { horizontal: 'right', vertical: 'middle' },
            border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } },
            numFmt: '#,##0.00;(#,##0.00);-'
        };
        const summaryNetStyle = {
            font: { bold: true, color: { argb: 'FF047857' }, name: 'Arial', size: 10 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } },
            alignment: { horizontal: 'right', vertical: 'middle' },
            border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } },
            numFmt: '#,##0.00;(#,##0.00);-'
        };

        // Row 1: Title
        sheet.addRow(["SUMMARY STATEMENT OF INVOICES RECEIVED \u2014 ANNEXURE B (NEW FORMAT)"]);
        sheet.mergeCells(1, 1, 1, 21);
        sheet.getRow(1).getCell(1).style = headerStyle;
        sheet.getRow(1).height = 30;

        // Row 2: Metadata
        sheet.addRow([`Name: ${tradeName || ""}    GSTIN: ${gstin || ""}    Period: ${fromPeriod || ""} TO ${toPeriod || ""}`]);
        sheet.mergeCells(2, 1, 2, 21);
        sheet.getRow(2).getCell(1).style = { ...headerStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }, font: { ...headerStyle.font, color: { argb: 'FF334155' } }, alignment: { horizontal: 'center', vertical: 'middle' } };
        sheet.getRow(2).height = 25;

        // Row 3: summary headers
        const summaryHeader = ["Particulars", "", "", "", "", "", "", "", "", "", "", "Total Central Tax", "Total State Tax / Union Territory Tax", "Total Integrated Tax", "Total Cess"];
        sheet.addRow(summaryHeader);
        sheet.mergeCells(3, 1, 3, 11);
        for (let c = 1; c <= 15; c++) sheet.getRow(3).getCell(c).style = summaryHeaderStyle;
        sheet.getRow(3).height = 36;

        // Row 4: Total ITC (1)
        sheet.addRow(["Total ITC as per Invoices mentioned in Utility (1)", "", "", "", "", "", "", "", "", "", "", summaryRow1.cgst, summaryRow1.sgst, summaryRow1.igst, summaryRow1.cess]);
        sheet.mergeCells(4, 1, 4, 11);
        sheet.getRow(4).getCell(1).style = summaryLabelStyle;
        for (let c = 12; c <= 15; c++) sheet.getRow(4).getCell(c).style = summaryValueStyle;
        sheet.getRow(4).height = 22;

        // Row 5: Reversal Sec 17(5) (2)
        sheet.addRow(["Reversal as per Rules 38, 42 & 43 of CGST Rules and Section 17(5) (2)", "", "", "", "", "", "", "", "", "", "", summaryRow2.cgst, summaryRow2.sgst, summaryRow2.igst, summaryRow2.cess]);
        sheet.mergeCells(5, 1, 5, 11);
        sheet.getRow(5).getCell(1).style = summaryLabelStyle;
        for (let c = 12; c <= 15; c++) sheet.getRow(5).getCell(c).style = summaryValueStyle;
        sheet.getRow(5).height = 22;

        // Row 6: Reversals Others (3)
        sheet.addRow(["Reversals - Others (3)", "", "", "", "", "", "", "", "", "", "", +reversalOthers.cgst || 0, +reversalOthers.sgst || 0, +reversalOthers.igst || 0, +reversalOthers.cess || 0]);
        sheet.mergeCells(6, 1, 6, 11);
        sheet.getRow(6).getCell(1).style = summaryLabelStyle;
        for (let c = 12; c <= 15; c++) {
            const cell = sheet.getRow(6).getCell(c);
            cell.style = { ...summaryValueStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEFCE8' } } };
        }
        sheet.getRow(6).height = 22;

        // Row 7: Net ITC (1)-(2)-(3)
        sheet.addRow(["Net Input Tax Credit after Reversals (1)-(2)-(3)", "", "", "", "", "", "", "", "", "", "", { formula: 'L4-L5-L6' }, { formula: 'M4-M5-M6' }, { formula: 'N4-N5-N6' }, { formula: 'O4-O5-O6' }]);
        sheet.mergeCells(7, 1, 7, 11);
        sheet.getRow(7).getCell(1).style = { ...summaryLabelStyle, font: { ...summaryLabelStyle.font, color: { argb: 'FF047857' } } };
        for (let c = 12; c <= 15; c++) sheet.getRow(7).getCell(c).style = summaryNetStyle;
        sheet.getRow(7).height = 24;

        // Row 8: blank separator
        sheet.addRow([]);
        sheet.getRow(8).height = 6;

        // Headers
        const headers = [
            "Sr. No.",
            "Type of Inward Supply",
            "Type of Document",
            "GSTIN of the Supplier",
            "Name of the Supplier",
            "Document No./ Bill of Entry",
            "Date (dd-mm-yyyy)",
            "Port Code",
            "Value",
            "Category of Supplies",
            "HSN/SAC",
            "Central Tax",
            "State Tax / Union Territory Tax",
            "Integrated Tax",
            "Cess",
            "Total Input Tax Credit (12+13+14+15)",
            "Whether ITC Blocked under Section 17(5)",
            "Eligible for ITC",
            "Amount of Eligible ITC",
            "Amount of Ineligible ITC",
            "2B Return Period"
        ];

        // Header row (row 9)
        const headerRow = sheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        headerRow.height = 40;

        // Freeze panes
        sheet.views = [{ state: 'frozen', ySplit: 9 }];

        // Pre-set Column 21 (Return Period) to Text format BEFORE adding data
        // so Excel never auto-detects "mm-yyyy" values as dates
        sheet.getColumn(21).numFmt = '@';

        // Data rows start at row 10
        const DATA_START_ROW = 10;
        dataRows.forEach((r, idx) => {
            const excelRow = DATA_START_ROW + idx;
            const itcFormula = { formula: `L${excelRow}+M${excelRow}+N${excelRow}+O${excelRow}` };
            const eligibleFormula = { formula: `P${excelRow}-T${excelRow}` };
            const row = sheet.addRow([
                idx + 1, r.inwardType, r.docType, r.gstin, r.supplierName, r.docNo, r.date,
                r.portCode, r.value, r.category, r.hsn, r.cgst, r.sgst, r.igst, r.cess,
                itcFormula, r.itcBlocked, r.eligibleItc, eligibleFormula, r.ineligibleItc || 0, String(r.returnPeriod || '')
            ]);
            row.font = { name: 'Arial', size: 10 };
            row.eachCell((cell) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            // Explicitly force cell U (col 21) as text to prevent date auto-detection
            const rpCell = row.getCell(21);
            rpCell.value = String(r.returnPeriod || '');
            rpCell.numFmt = '@';
        });

        // Number formatting
        const numericCols = [9, 12, 13, 14, 15, 16, 19, 20];
        const numFmt = '#,##0.00;(#,##0.00);-';
        sheet.eachRow((row, rowNumber) => {
            if (rowNumber >= DATA_START_ROW) {
                numericCols.forEach(c => {
                    const cell = row.getCell(c);
                    cell.numFmt = numFmt;
                    cell.alignment = { horizontal: 'right' };
                });
                row.getCell(7).alignment = { horizontal: 'center' };
                row.getCell(1).alignment = { horizontal: 'center' };
                // Safety: re-enforce text format on Column 21
                const rpCell = row.getCell(21);
                rpCell.numFmt = '@';
                rpCell.alignment = { horizontal: 'center' };
            }
        });

        // Column widths
        const widthMap = [6, 36, 22, 22, 30, 22, 14, 10, 14, 22, 14, 14, 14, 14, 12, 18, 22, 16, 18, 18, 14];
        widthMap.forEach((w, i) => { sheet.getColumn(i + 1).width = Math.min(w, 40); });

        // Dropdowns
        const lastDataRow = DATA_START_ROW + dataRows.length - 1;
        if (lastDataRow >= DATA_START_ROW) {
            const inwardOpts = '"Import of Goods,Import of Services,Inward Supplies from ISD,Inward Supplies liable to Reverse Charge (Unregistered Persons),Inward Supplies liable to Reverse Charge (Registered Persons),Inward Supplies from Registered Persons,Import of Services(Supplies from SEZ to DTA),Import of Goods(Supplies from SEZ to DTA)"';
            const docOpts = '"Invoice/Bill of Entry,Credit Note,Debit Note"';
            const categoryOpts = '"Inputs,Input Services,Capital Goods"';
            const yesNoPartialOpts = '"Yes,No,Partially"';

            const applyDV = (colLetter, formula1) => {
                for (let r = DATA_START_ROW; r <= lastDataRow; r++) {
                    sheet.getCell(`${colLetter}${r}`).dataValidation = {
                        type: 'list', allowBlank: true, formulae: [formula1], showErrorMessage: false
                    };
                }
            };

            applyDV('B', inwardOpts);
            applyDV('C', docOpts);
            applyDV('J', categoryOpts);
            applyDV('Q', yesNoPartialOpts);
            applyDV('R', yesNoPartialOpts);
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const fname = `Annexure_B_New_${gstin || 'output'}.xlsx`;
        saveAs(new Blob([buffer]), fname);
    };

    // ── Download as PDF ──
    const downloadAnnexureBPdf = (meta, rows, revOthers) => {
        if (!rows || rows.length === 0) return;

        const { tradeName, gstin, fromPeriod, toPeriod } = meta;

        const COLOR = {
            headerBg:    [30, 58, 138],
            headerText:  [255, 255, 255],
            summaryBg:   [236, 253, 245],
            altRow:      [248, 250, 252],
            borderColor: [203, 213, 225],
            totalRow:    [254, 252, 232],
            netGreen:    [4, 120, 87],
            bodyText:    [51, 65, 85],
            labelBg:     [241, 245, 249],
        };

        const fmtINR = (val) => {
            const n = parseFloat(val);
            if (isNaN(n) || n === 0) return '0.00';
            return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
        };

        const tCgst  = rows.reduce((a, r) => a + (r.cgst  || 0), 0);
        const tSgst  = rows.reduce((a, r) => a + (r.sgst  || 0), 0);
        const tIgst  = rows.reduce((a, r) => a + (r.igst  || 0), 0);
        const tCess  = rows.reduce((a, r) => a + (r.cess  || 0), 0);

        const bRows = rows.filter(r => r.itcBlocked === 'Yes');
        const r2Cgst = bRows.reduce((a, r) => a + (r.cgst || 0), 0);
        const r2Sgst = bRows.reduce((a, r) => a + (r.sgst || 0), 0);
        const r2Igst = bRows.reduce((a, r) => a + (r.igst || 0), 0);
        const r2Cess = bRows.reduce((a, r) => a + (r.cess || 0), 0);

        const roCgst = +revOthers.cgst || 0;
        const roSgst = +revOthers.sgst || 0;
        const roIgst = +revOthers.igst || 0;
        const roCess = +revOthers.cess || 0;

        const netCgst = tCgst - r2Cgst - roCgst;
        const netSgst = tSgst - r2Sgst - roSgst;
        const netIgst = tIgst - r2Igst - roIgst;
        const netCess = tCess - r2Cess - roCess;

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
        const PAGE_W  = doc.internal.pageSize.getWidth();
        const MARGIN  = 8;
        const CONTENT_W = PAGE_W - MARGIN * 2;
        let cursorY = MARGIN;

        const band = (text, bgRGB, textRGB, fontSize, heightMm) => {
            doc.setFillColor(...bgRGB);
            doc.rect(MARGIN, cursorY, CONTENT_W, heightMm, 'F');
            doc.setFontSize(fontSize);
            doc.setTextColor(...textRGB);
            doc.setFont('helvetica', 'bold');
            doc.text(text, PAGE_W / 2, cursorY + heightMm / 2 + fontSize * 0.18, { align: 'center', baseline: 'middle' });
            cursorY += heightMm;
        };

        band('SUMMARY STATEMENT OF INVOICES RECEIVED \u2014 ANNEXURE B (NEW FORMAT)', COLOR.headerBg, COLOR.headerText, 11, 9);
        band(`Name: ${tradeName || ''}    GSTIN: ${gstin || ''}    Period: ${fromPeriod || ''} TO ${toPeriod || ''}`, COLOR.labelBg, COLOR.bodyText, 8, 7);

        const summaryTableTop = cursorY + 1;
        const SUM_COL_W = [CONTENT_W * 0.55, ...Array(4).fill(CONTENT_W * 0.45 / 4)];
        const summaryRows = [
            { label: 'Total ITC as per Invoices mentioned in Utility (1)', cgst: tCgst, sgst: tSgst, igst: tIgst, cess: tCess, style: 'normal' },
            { label: 'Reversal as per Rules 38, 42 & 43 and Section 17(5) (2)', cgst: r2Cgst, sgst: r2Sgst, igst: r2Igst, cess: r2Cess, style: 'normal' },
            { label: 'Reversals - Others (3)', cgst: roCgst, sgst: roSgst, igst: roIgst, cess: roCess, style: 'yellow' },
            { label: 'Net Input Tax Credit after Reversals (1)\u2212(2)\u2212(3)', cgst: netCgst, sgst: netSgst, igst: netIgst, cess: netCess, style: 'net' }
        ];

        autoTable(doc, {
            startY: summaryTableTop, margin: { left: MARGIN, right: MARGIN }, tableWidth: CONTENT_W,
            head: [[{ content: 'Particulars', styles: { halign: 'left' } }, 'Total Central Tax', 'Total State/UT Tax', 'Total Integrated Tax', 'Total Cess']],
            body: summaryRows.map(r => [r.label, fmtINR(r.cgst), fmtINR(r.sgst), fmtINR(r.igst), fmtINR(r.cess)]),
            headStyles: { fillColor: COLOR.headerBg, textColor: COLOR.headerText, fontStyle: 'bold', fontSize: 7.5, halign: 'center', valign: 'middle', cellPadding: 2 },
            bodyStyles: { fontSize: 7.5, cellPadding: 1.8, textColor: COLOR.bodyText, lineColor: COLOR.borderColor, lineWidth: 0.2 },
            columnStyles: {
                0: { cellWidth: SUM_COL_W[0], fontStyle: 'bold', fillColor: COLOR.labelBg },
                1: { cellWidth: SUM_COL_W[1], halign: 'right' },
                2: { cellWidth: SUM_COL_W[2], halign: 'right' },
                3: { cellWidth: SUM_COL_W[3], halign: 'right' },
                4: { cellWidth: SUM_COL_W[4], halign: 'right' },
            },
            willDrawCell: (data) => {
                if (data.section === 'body') {
                    const r = summaryRows[data.row.index];
                    if (r.style === 'yellow') doc.setFillColor(254, 252, 232);
                    else if (r.style === 'net') {
                        doc.setFillColor(...COLOR.summaryBg);
                        doc.setTextColor(...COLOR.netGreen);
                    }
                }
            }
        });

        cursorY = doc.lastAutoTable.finalY + 4;

        const HEADERS_21 = ['Sr.', 'Type of Inward Supply', 'Type of Document', 'GSTIN of Supplier', 'Name of Supplier', 'Document No./Bill of Entry', 'Date\n(dd-mm-yyyy)', 'Port Code', 'Value', 'Category of Supplies', 'HSN/SAC', 'Central Tax', 'State/UT Tax', 'Integrated Tax', 'Cess', 'Total ITC', 'ITC Blocked\nu/s 17(5)', 'Eligible\nfor ITC', 'Eligible ITC\nAmount', 'Ineligible ITC\nAmount', '2B Return\nPeriod'];
        const COL_WIDTHS = [8, 38, 22, 28, 30, 26, 18, 12, 18, 20, 14, 16, 16, 16, 14, 18, 16, 14, 18, 18, 14];

        const bodyData = rows.map((r, idx) => {
            const itc = (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0) + (r.cess || 0);
            const eligAmt = r.eligibleItc === 'Yes' ? itc : r.eligibleItc === 'No' ? 0 : itc - (r.ineligibleItc || 0);
            return [idx + 1, r.inwardType || '', r.docType || '', r.gstin || '', r.supplierName || '', r.docNo || '', r.date || '', r.portCode || '', fmtINR(r.value), r.category || '', r.hsn || '', fmtINR(r.cgst), fmtINR(r.sgst), fmtINR(r.igst), fmtINR(r.cess), fmtINR(itc), r.itcBlocked || 'No', r.eligibleItc || 'Yes', fmtINR(eligAmt), fmtINR(r.ineligibleItc || 0), r.returnPeriod || ''];
        });

        const totalItcSum = rows.reduce((a, r) => a + (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0) + (r.cess || 0), 0);
        const footData = [['', 'GRAND TOTAL', '', '', '', '', '', '', fmtINR(rows.reduce((a, r) => a + (r.value || 0), 0)), '', '', fmtINR(tCgst), fmtINR(tSgst), fmtINR(tIgst), fmtINR(tCess), fmtINR(totalItcSum), '', '', '', '', '']];

        const colStylesObj = {};
        COL_WIDTHS.forEach((w, i) => { colStylesObj[i] = { cellWidth: w, halign: [8, 11, 12, 13, 14, 15, 18, 19].includes(i) ? 'right' : i === 0 ? 'center' : 'left', fontSize: 6.5 }; });

        autoTable(doc, {
            startY: cursorY, margin: { left: MARGIN, right: MARGIN }, tableWidth: CONTENT_W,
            head: [HEADERS_21], body: bodyData, foot: footData,
            headStyles: { fillColor: COLOR.headerBg, textColor: COLOR.headerText, fontStyle: 'bold', fontSize: 6.5, halign: 'center', valign: 'middle', cellPadding: 1.5, minCellHeight: 10 },
            bodyStyles: { fontSize: 6.5, cellPadding: 1.2, textColor: COLOR.bodyText, lineColor: COLOR.borderColor, lineWidth: 0.15, overflow: 'linebreak' },
            footStyles: { fillColor: COLOR.totalRow, textColor: COLOR.bodyText, fontStyle: 'bold', fontSize: 6.5, cellPadding: 1.5 },
            alternateRowStyles: { fillColor: COLOR.altRow },
            columnStyles: colStylesObj,
            showHead: 'everyPage', showFoot: 'lastPage',
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(7); doc.setTextColor(150); doc.setFont('helvetica', 'normal');
                doc.text(`Page ${currentPage} of ${pageCount}`, PAGE_W - MARGIN, doc.internal.pageSize.getHeight() - 4, { align: 'right' });
                doc.text(`Annexure B \u2014 ${tradeName || ''} (${gstin || ''})  |  Period: ${fromPeriod} to ${toPeriod}`, MARGIN, doc.internal.pageSize.getHeight() - 4, { align: 'left' });
            }
        });

        doc.save(`Annexure_B_New_${gstin || 'output'}.pdf`);
    };

    const copyToTSV = () => {
        if (dataRows.length === 0) return;
        const headers = [
            "Sr. No.", "Type of Inward Supply", "Type of Document", "GSTIN of the Supplier",
            "Name of the Supplier", "Document No./ Bill of Entry", "Date", "Port Code", "Value",
            "Category of Supplies", "HSN/SAC", "Central Tax", "State Tax", "Integrated Tax", "Cess",
            "Total ITC", "ITC Blocked u/s 17(5)", "Eligible for ITC", "Amount of Eligible ITC",
            "Amount of Ineligible ITC", "2B Return Period"
        ];
        const rows = dataRows.map((r, i) => {
            const itc = r.cgst + r.sgst + r.igst + r.cess;
            return [
                i + 1, r.inwardType, r.docType, r.gstin, r.supplierName, r.docNo, r.date,
                r.portCode, r.value, r.category, r.hsn, r.cgst, r.sgst, r.igst, r.cess, itc,
                r.itcBlocked, r.eligibleItc, itc, r.ineligibleItc, r.returnPeriod
            ];
        });
        const tsv = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(tsv).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const getRowBg = (source) => {
        if (source === 'B2B') return 'rgba(245, 158, 11, 0.08)';
        if (source === 'CDNR') return 'rgba(251, 113, 133, 0.08)';
        if (source === 'IMPG') return 'rgba(45, 212, 191, 0.08)';
        if (source === 'IMPG_SEZ') return 'rgba(167, 139, 250, 0.08)';
        return 'transparent';
    };

    const totalCgst = dataRows.reduce((a, r) => a + r.cgst, 0);
    const totalSgst = dataRows.reduce((a, r) => a + r.sgst, 0);
    const totalIgst = dataRows.reduce((a, r) => a + r.igst, 0);
    const totalCess = dataRows.reduce((a, r) => a + r.cess, 0);
    const totalItc = totalCgst + totalSgst + totalIgst + totalCess;
    const totalValue = dataRows.reduce((a, r) => a + r.value, 0);

    // ── Summary Reversal Table computations ───────────────────────────────
    // Row 2: Total ITC as per Invoices (sum of all rows — credit notes are
    //         stored as negative so they auto-deduct)
    const summaryRow1 = {
        cgst: totalCgst,
        sgst: totalSgst,
        igst: totalIgst,
        cess: totalCess
    };

    // Row 3: Reversal as per Rule 38/42/43 + Sec 17(5) — rows where Col 17 = "Yes"
    const blockedRows = dataRows.filter(r => r.itcBlocked === "Yes");
    const summaryRow2 = {
        cgst: blockedRows.reduce((a, r) => a + (r.cgst || 0), 0),
        sgst: blockedRows.reduce((a, r) => a + (r.sgst || 0), 0),
        igst: blockedRows.reduce((a, r) => a + (r.igst || 0), 0),
        cess: blockedRows.reduce((a, r) => a + (r.cess || 0), 0)
    };

    // Row 4: Reversal - Others (manual)
    const summaryRow3 = reversalOthers;

    // Row 5: Net ITC = Row 2 - Row 3 - Row 4
    const summaryNet = {
        cgst: summaryRow1.cgst - summaryRow2.cgst - (+summaryRow3.cgst || 0),
        sgst: summaryRow1.sgst - summaryRow2.sgst - (+summaryRow3.sgst || 0),
        igst: summaryRow1.igst - summaryRow2.igst - (+summaryRow3.igst || 0),
        cess: summaryRow1.cess - summaryRow2.cess - (+summaryRow3.cess || 0)
    };

    const updateReversalOthers = (field, value) => {
        const n = parseFloat(value);
        setReversalOthers(prev => ({ ...prev, [field]: isNaN(n) ? 0 : n }));
    };

    return (
        <div className="annexure-b-container">
            <style>{`
                .annexure-b-container {
                    background: #ffffff; color: #1e293b;
                    font-family: 'DM Mono', monospace;
                    padding: 24px; border-radius: 12px;
                }
                .syne { font-family: 'Syne', sans-serif; }
                .input-panel {
                    background: #f8fafc; border: 1px solid #e2e8f0;
                    padding: 16px; border-radius: 8px; margin-bottom: 20px;
                    display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px;
                }
                .input-item label { color: #64748b; font-size: 12px; display: block; margin-bottom: 4px; }
                .input-item input {
                    background: #fff; border: 1px solid #cbd5e1; color: #1e293b;
                    padding: 8px; border-radius: 4px; width: 100%; font-size: 13px;
                }
                .upload-zone {
                    border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px;
                    text-align: center; cursor: pointer; margin-bottom: 20px; transition: border-color 0.3s;
                }
                .upload-zone:hover { border-color: #1e3a8a; }
                .stats-bar {
                    background: #f8fafc; padding: 12px 16px; border-radius: 6px;
                    margin-bottom: 16px; display: flex; flex-wrap: wrap; gap: 16px;
                    font-size: 12px; border: 1px solid #e2e8f0;
                }
                .stat-tag { border-right: 1px solid #e2e8f0; padding-right: 16px; color: #1e293b; }
                .stat-tag span { color: #1e3a8a; font-weight: bold; }
                .table-container {
                    background: #fff; border: 1px solid #e2e8f0;
                    border-radius: 8px; overflow: hidden;
                }
                .table-wrap { overflow: auto; max-height: 560px; width: 100%; }
                table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 2000px; }
                th { background: #1e3a8a; color: #fff; padding: 12px 8px; font-size: 11px;
                    text-align: left; position: sticky; top: 0; z-index: 10; border-bottom: 2px solid #1e293b; }
                td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #334155; }
                .grand-total { background: rgba(16, 185, 129, 0.12); font-weight: bold; }
                .btn-copy {
                    background: #fff; border: 1px solid #e2e8f0; color: #64748b; padding: 6px 12px;
                    border-radius: 4px; cursor: pointer; margin: 12px; display: flex; align-items: center; gap: 6px;
                }
                .btn-download {
                    background: #1e3a8a; color: #fff; border: none; padding: 8px 20px;
                    border-radius: 6px; cursor: pointer; font-weight: bold;
                }
                .btn-download:disabled { background: #94a3b8; cursor: not-allowed; }
                .btn-json { background: #047857; }
                .btn-json:disabled { background: #94a3b8; }
                .loader-overlay {
                    position: fixed; inset: 0; background: rgba(255,255,255,0.8);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .spinner { width: 40px; height: 40px; border: 3px solid #e2e8f0;
                    border-top-color: #1e3a8a; border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .staggered-row { animation: fadeIn 0.3s ease forwards; opacity: 0; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
                select.inline {
                    border: 1px solid #e2e8f0; border-radius: 4px;
                    background: #f8fafc; padding: 3px; font-size: 10px; outline: none;
                }
                /* ── Summary Reversal Table ── */
                .summary-table-wrap {
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    overflow-x: auto;
                    overflow-y: hidden;
                    margin-bottom: 14px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
                    max-width: 100%;
                    box-sizing: border-box;
                }
                .summary-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 11px;
                    table-layout: fixed;
                }
                .summary-table thead th {
                    background: #1e3a8a;
                    color: #fff;
                    padding: 6px 6px;
                    text-align: center;
                    font-weight: bold;
                    font-size: 10.5px;
                    line-height: 1.25;
                    border: 1px solid #1e293b;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    white-space: normal;
                }
                .summary-table thead th:first-child { text-align: left; }
                .summary-table tbody td {
                    padding: 5px 6px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    line-height: 1.3;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                .summary-table .summary-label {
                    background: #f8fafc;
                    font-weight: 500;
                    font-size: 11px;
                }
                .summary-table .summary-val {
                    text-align: right;
                    font-variant-numeric: tabular-nums;
                    font-size: 11.5px;
                }
                .summary-table .summary-input-cell {
                    padding: 2px 3px;
                    background: #fefce8;
                }
                .summary-table .summary-input {
                    width: 100%;
                    border: 1px solid #fde047;
                    background: #fff;
                    padding: 3px 5px;
                    border-radius: 3px;
                    text-align: right;
                    font-size: 11px;
                    font-family: inherit;
                    outline: none;
                    box-sizing: border-box;
                }
                .summary-table .summary-input:focus {
                    border-color: #f59e0b;
                    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
                }
                .summary-table .summary-net-row {
                    background: linear-gradient(90deg, #ecfdf5, #f0fdf4);
                    font-weight: bold;
                }
                .summary-table .summary-net-row .summary-label { background: transparent; }
                .summary-table .summary-val.net { color: #047857; font-weight: bold; font-size: 12px; }
                .editable-amt-input {
                    width: 100%; box-sizing: border-box;
                    border: 1px solid #fde047; background: #fefce8;
                    padding: 3px 5px; border-radius: 3px; text-align: right;
                    font-size: 11px; font-family: inherit; outline: none;
                }
                .editable-amt-input:focus {
                    border-color: #f59e0b; background: #fff;
                    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
                }
                .locked-amt {
                    color: #64748b; font-style: normal;
                }
            `}</style>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 className="syne" style={{ margin: 0, color: '#1e3a8a' }}>
                    Annexure B Module — New 21-Column Format
                </h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        className="btn-download btn-json"
                        onClick={handleDownloadJSON}
                        disabled={dataRows.length === 0}
                        title="Download in GST-portal Annexure B JSON format"
                    >
                        ⬇ Download JSON
                    </button>
                    <button className="btn-download" onClick={handleDownload} disabled={dataRows.length === 0}>
                        ⬇ Download Excel
                    </button>
                    <button
                        className="btn-download"
                        style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
                        onClick={() => downloadAnnexureBPdf(metadata, dataRows, reversalOthers)}
                        disabled={dataRows.length === 0}
                    >
                        ⬇ Download PDF
                    </button>
                </div>
            </div>

            <div className="input-panel">
                <div className="input-item">
                    <label>Trade Name</label>
                    <input value={metadata.tradeName} onChange={e => setMetadata({ ...metadata, tradeName: e.target.value })} />
                </div>
                <div className="input-item">
                    <label>GSTIN</label>
                    <input value={metadata.gstin} onChange={e => setMetadata({ ...metadata, gstin: e.target.value })} />
                </div>
                <div className="input-item">
                    <label>From Period</label>
                    <input placeholder="e.g. 042025" value={metadata.fromPeriod} onChange={e => setMetadata({ ...metadata, fromPeriod: e.target.value })} />
                </div>
                <div className="input-item">
                    <label>To Period</label>
                    <input placeholder="e.g. 032026" value={metadata.toPeriod} onChange={e => setMetadata({ ...metadata, toPeriod: e.target.value })} />
                </div>
            </div>

            <div className="upload-zone" onClick={() => fileInputRef.current?.click()}>
                <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleUpload} />
                <AiOutlineCloudUpload size={48} color="#1e3a8a" style={{ marginBottom: '12px' }} />
                <div className="syne" style={{ fontSize: '18px' }}>Drop GSTR-2B Excel Here</div>
                <div style={{ color: '#64748b', fontSize: '13px' }}>
                    Reads B2B, B2B-CDNR, IMPG, IMPG-SEZ from row 7 onward
                </div>
            </div>

            {loading && <div className="loader-overlay"><div className="spinner" /></div>}

            {dataRows.length > 0 && (
                <>
                    <div className="stats-bar">
                        <div className="stat-tag">B2B: <span>{stats.B2B}</span></div>
                        <div className="stat-tag">CDNR: <span>{stats.CDNR}</span></div>
                        <div className="stat-tag">IMPG: <span>{stats.IMPG}</span></div>
                        <div className="stat-tag">SEZ: <span>{stats.IMPG_SEZ}</span></div>
                        <div className="stat-tag">Blank-GSTIN → 9965: <span>{stats.blankGstinFallback}</span></div>
                        <div className="stat-tag" style={{ border: 0 }}>
                            Value: <span>₹{formatINR(totalValue)}</span> |
                            CGST: <span>₹{formatINR(totalCgst)}</span> |
                            SGST: <span>₹{formatINR(totalSgst)}</span> |
                            IGST: <span>₹{formatINR(totalIgst)}</span> |
                            Cess: <span>₹{formatINR(totalCess)}</span> |
                            ITC: <span>₹{formatINR(totalItc)}</span>
                        </div>
                    </div>

                    {/* ───────────────── Summary Reversal Table ───────────────── */}
                    <div className="summary-table-wrap">
                        <table className="summary-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40%' }}>Particulars</th>
                                    <th>Total Central Tax</th>
                                    <th>Total State Tax / Union Territory Tax</th>
                                    <th>Total Integrated Tax</th>
                                    <th>Total Cess</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="summary-label">
                                        Total ITC as per Invoices mentioned in Utility <strong>(1)</strong>
                                    </td>
                                    <td className="summary-val">{formatINR(summaryRow1.cgst)}</td>
                                    <td className="summary-val">{formatINR(summaryRow1.sgst)}</td>
                                    <td className="summary-val">{formatINR(summaryRow1.igst)}</td>
                                    <td className="summary-val">{formatINR(summaryRow1.cess)}</td>
                                </tr>
                                <tr>
                                    <td className="summary-label">
                                        Reversal as per Rules 38, 42 &amp; 43 of CGST Rules and Section 17(5) <strong>(2)</strong>
                                    </td>
                                    <td className="summary-val">{formatINR(summaryRow2.cgst)}</td>
                                    <td className="summary-val">{formatINR(summaryRow2.sgst)}</td>
                                    <td className="summary-val">{formatINR(summaryRow2.igst)}</td>
                                    <td className="summary-val">{formatINR(summaryRow2.cess)}</td>
                                </tr>
                                <tr>
                                    <td className="summary-label">
                                        Reversals - Others <strong>(3)</strong>
                                    </td>
                                    <td className="summary-input-cell">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="summary-input"
                                            value={reversalOthers.cgst || ""}
                                            placeholder="0.00"
                                            onChange={(e) => updateReversalOthers('cgst', e.target.value)}
                                        />
                                    </td>
                                    <td className="summary-input-cell">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="summary-input"
                                            value={reversalOthers.sgst || ""}
                                            placeholder="0.00"
                                            onChange={(e) => updateReversalOthers('sgst', e.target.value)}
                                        />
                                    </td>
                                    <td className="summary-input-cell">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="summary-input"
                                            value={reversalOthers.igst || ""}
                                            placeholder="0.00"
                                            onChange={(e) => updateReversalOthers('igst', e.target.value)}
                                        />
                                    </td>
                                    <td className="summary-input-cell">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="summary-input"
                                            value={reversalOthers.cess || ""}
                                            placeholder="0.00"
                                            onChange={(e) => updateReversalOthers('cess', e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr className="summary-net-row">
                                    <td className="summary-label">
                                        Net Input Tax Credit after Reversals <strong>(1)-(2)-(3)</strong>
                                    </td>
                                    <td className="summary-val net">{formatINR(summaryNet.cgst)}</td>
                                    <td className="summary-val net">{formatINR(summaryNet.sgst)}</td>
                                    <td className="summary-val net">{formatINR(summaryNet.igst)}</td>
                                    <td className="summary-val net">{formatINR(summaryNet.cess)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="table-container">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Sr</th>
                                        <th>Type of Inward Supply</th>
                                        <th>Doc Type</th>
                                        <th>GSTIN</th>
                                        <th>Supplier Name</th>
                                        <th>Doc No.</th>
                                        <th>Date</th>
                                        <th>Port</th>
                                        <th>Value</th>
                                        <th>Category</th>
                                        <th>HSN/SAC</th>
                                        <th>CGST</th>
                                        <th>SGST</th>
                                        <th>IGST</th>
                                        <th>Cess</th>
                                        <th>Total ITC</th>
                                        <th>Blocked</th>
                                        <th>Eligible</th>
                                        <th>Elig. ITC</th>
                                        <th>Inelig. ITC</th>
                                        <th>Return Period</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataRows.map((r, i) => {
                                        const totalItcRow = r.cgst + r.sgst + r.igst + r.cess;
                                        return (
                                            <tr key={i} className="staggered-row"
                                                style={{ animationDelay: `${i * 0.01}s`, background: getRowBg(r.source) }}>
                                                <td>{i + 1}</td>
                                                <td style={{ fontSize: '10px' }}>{r.inwardType}</td>
                                                <td>{r.docType}</td>
                                                <td>{r.gstin || <em style={{ color: '#ef4444' }}>BLANK</em>}</td>
                                                <td>{r.supplierName}</td>
                                                <td>{r.docNo}</td>
                                                <td>{r.date}</td>
                                                <td>{r.portCode}</td>
                                                <td style={{ textAlign: 'right' }}>{formatINR(r.value)}</td>
                                                <td>
                                                    <select className="inline" value={r.category}
                                                        onChange={(e) => updateRowField(i, 'category', e.target.value)}>
                                                        <option value="Inputs">Inputs</option>
                                                        <option value="Input Services">Input Services</option>
                                                        <option value="Capital Goods">Capital Goods</option>
                                                    </select>
                                                </td>
                                                <td style={{ fontWeight: !r.gstin ? 'bold' : 'normal', color: !r.gstin ? '#1e3a8a' : 'inherit' }}>
                                                    {r.hsn}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>{formatINR(r.cgst)}</td>
                                                <td style={{ textAlign: 'right' }}>{formatINR(r.sgst)}</td>
                                                <td style={{ textAlign: 'right' }}>{formatINR(r.igst)}</td>
                                                <td style={{ textAlign: 'right' }}>{formatINR(r.cess)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatINR(totalItcRow)}</td>
                                                <td>
                                                    <select className="inline" value={r.itcBlocked}
                                                        onChange={(e) => handleItcBlockedChange(i, e.target.value)}>
                                                        <option value="No">No</option>
                                                        <option value="Yes">Yes</option>
                                                        <option value="Partially">Partially</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <select className="inline" value={r.eligibleItc}
                                                        onChange={(e) => handleEligibleItcChange(i, e.target.value)}>
                                                        <option value="Yes">Yes</option>
                                                        <option value="No">No</option>
                                                        <option value="Partially">Partially</option>
                                                    </select>
                                                </td>
                                                {(() => {
                                                    const editable = isAmtEditable(r);
                                                    const eligAmt = Math.round((totalItcRow - (r.ineligibleItc || 0)) * 100) / 100;
                                                    return (
                                                        <>
                                                            <td style={{ textAlign: 'right', padding: editable ? '2px 4px' : '6px 8px' }}>
                                                                {editable ? (
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        className="editable-amt-input"
                                                                        value={eligAmt || ""}
                                                                        placeholder="0.00"
                                                                        onChange={(e) => handleEligibleAmtChange(i, e.target.value)}
                                                                    />
                                                                ) : (
                                                                    <span className={eligAmt === 0 ? "locked-amt" : ""}>
                                                                        {formatINR(eligAmt)}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ textAlign: 'right', padding: editable ? '2px 4px' : '6px 8px' }}>
                                                                {editable ? (
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        className="editable-amt-input"
                                                                        value={r.ineligibleItc || ""}
                                                                        placeholder="0.00"
                                                                        onChange={(e) => handleIneligibleAmtChange(i, e.target.value)}
                                                                    />
                                                                ) : (
                                                                    <span className={(r.ineligibleItc || 0) === 0 ? "locked-amt" : ""}>
                                                                        {formatINR(r.ineligibleItc || 0)}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </>
                                                    );
                                                })()}
                                                <td>{r.returnPeriod}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="grand-total">
                                        <td colSpan={8}>GRAND TOTAL</td>
                                        <td style={{ textAlign: 'right' }}>{formatINR(totalValue)}</td>
                                        <td colSpan={2}></td>
                                        <td style={{ textAlign: 'right' }}>{formatINR(totalCgst)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatINR(totalSgst)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatINR(totalIgst)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatINR(totalCess)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatINR(totalItc)}</td>
                                        <td colSpan={2}></td>
                                        <td style={{ textAlign: 'right' }}>{formatINR(totalItc)}</td>
                                        <td colSpan={2}></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <button className="btn-copy" onClick={copyToTSV}>
                            {copied ? <AiOutlineCheck color="#34d399" /> : <AiOutlineCopy />}
                            {copied ? 'Copied ✓' : 'Copy TSV (21 cols)'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AnnexureBModule;
